import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';
import { validateResourceUrl } from '../utils/validation.js';

export interface GoogleGeminiImageParams {
  /**
   * 图像生成提示词，长度必须在1-2500字符之间
   */
  prompt: string;
  /**
   * 安全设置
   * @default "block_few"
   */
  safety_settings?: string;
  /**
   * 生成配置
   */
  generation_config?: Record<string, any>;
  /**
   * 输入图像URL列表，最多支持3个图像
   */
  image_urls?: string[];
  /**
   * 图像宽高比，支持: 1:1, 2:3, 3:2, 3:4, 4:3, 4:5, 5:4, 9:16, 16:9, 21:9
   * @values 1:1 | 2:3 | 3:2 | 3:4 | 4:3 | 4:5 | 5:4 | 9:16 | 16:9 | 21:9
   */
  aspect_ratio?: '1:1' | '2:3' | '3:2' | '3:4' | '4:3' | '4:5' | '5:4' | '9:16' | '16:9' | '21:9';
}

export interface GoogleGeminiImageResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * google_gemini_image
 *
 * Google Gemini 图像生成实现。
 * 
 * Model: gemini-2.5-flash-image
 * Features: 文本生成图像 (Text-to-Image), 图像编辑 (Image Editing), 多图像合成 (Multi-Image Composition)
 * Input Images: 最多支持3张图像
 * Aspect Ratios: 1:1, 2:3, 3:2, 3:4, 4:3, 4:5, 5:4, 9:16, 16:9, 21:9
 *
 * @example
 * ```typescript
 * import { initSeacloud, googleGeminiImage } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await googleGeminiImage({
 *   // 参数见 GoogleGeminiImageParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 GoogleGeminiImageParams 接口定义
 * @returns 生成的资源列表
 */
export async function googleGeminiImage(
  params: GoogleGeminiImageParams,
): Promise<GoogleGeminiImageResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'google_gemini_image',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: GoogleGeminiImageResource[] = [];
  let firstTextResource: any = null;

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          // Skip text resources - only process image/video resources
          // Google Gemini API sometimes returns text descriptions alongside media
          if (resource.type === 'text') {
            if (!firstTextResource) {
              firstTextResource = resource;
            }
            continue;
          }

          // Validate non-text resources
          validateResourceUrl(resource, 'image');

          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  // If no valid resources found after filtering, throw error with text info if available
  if (resources.length === 0) {
    const errorResource = firstTextResource || { type: 'text', url: '' };
    validateResourceUrl(errorResource, 'image');
  }

  return resources;
}
